/**
 * Filter buttons component for user and account filtering
 * Extracted from Activity.tsx to improve code organization
 */

import { ReactElement, MouseEvent, useState, useMemo, useRef, useEffect, useCallback } from "react";
import {
  Button,
  Popover,
  Box,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { WorkspaceMember } from "../hooks/useWorkspaceData";
import FloatingLabelFilterButton, { FilterOption } from "../../../components/FloatingLabelFilterButton";
import { isBypassAuthEnabled } from "../../../utils/bypassAuth";

// Minimal account type for filter buttons (only needs id, accountName, and group)
export interface FilterAccount {
  id: string;
  accountName: string;
  group?: string;
}

export interface FilterButtonsProps {
  // Accounts
  accounts: FilterAccount[];
  selectedAccountFilters: string[];
  onAccountFiltersChange: (filters: string[]) => void;
  getAccountDisplayName: (accountId: string) => string;
  
  // Users
  workspaceMembers: WorkspaceMember[];
  selectedUsers: string[];
  onUsersChange: (users: string[]) => void;
  getUserName: (userId: string) => string;
  transactions?: Array<{ userId?: string }>; // Optional: to check which users have created transactions (minimal type needed)
  
  // Edited checkbox
  showOnlyEdited: boolean;
  onShowOnlyEditedChange: (show: boolean) => void;
  
  // Control which filters to render (for mobile layout where they need to be in separate Grid cells)
  showAccountFilter?: boolean;
  showUserFilter?: boolean;
  showEditedCheckbox?: boolean;
}

/**
 * Filter buttons component for accounts and users
 */
export default function FilterButtons({
  accounts,
  selectedAccountFilters,
  onAccountFiltersChange,
  getAccountDisplayName,
  workspaceMembers,
  selectedUsers,
  onUsersChange,
  getUserName,
  transactions = [],
  showOnlyEdited,
  onShowOnlyEditedChange,
  showAccountFilter = true,
  showUserFilter = true,
  showEditedCheckbox = false,
}: FilterButtonsProps): ReactElement {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [accountFilterAnchor, setAccountFilterAnchor] = useState<HTMLElement | null>(null);
  const [userFilterAnchor, setUserFilterAnchor] = useState<HTMLElement | null>(null);

  // Helper functions for filtering users (defined at component level for reuse)
  const isOrphanedUserId = (userId: string): boolean => userId.startsWith("Ao7fhBrt");
  const isFakeUserId = (userId: string): boolean => {
    return ["demo-user"].includes(userId); // Removed dev-user-developer - we use bypass-user now
  };

  const handleAccountFilterClick = (event: MouseEvent<HTMLElement>): void => {
    setAccountFilterAnchor(event.currentTarget);
    // Blur the button to remove focus after click
    if (event.currentTarget instanceof HTMLElement) {
      event.currentTarget.blur();
    }
  };

  const handleAccountFilterClose = (): void => {
    setAccountFilterAnchor(null);
  };

  const handleAccountToggle = (accountId: string): void => {
    onAccountFiltersChange(
      selectedAccountFilters.includes(accountId)
        ? selectedAccountFilters.filter(id => id !== accountId)
        : [...selectedAccountFilters, accountId]
    );
  };

  const handleSelectAllAccounts = (): void => {
    // Toggle: if all accounts are selected, clear to empty (show all)
    // Otherwise, select all accounts
    // Empty array = show all accounts (no filter) - matches Analytics behavior
    onAccountFiltersChange(
      selectedAccountFilters.length === accounts.length
        ? [] // Empty array = show all (no filter)
        : accounts.map(acc => acc.id) // Select all accounts
    );
  };

  const handleUserFilterClick = (event: MouseEvent<HTMLElement>): void => {
    setUserFilterAnchor(event.currentTarget);
    // Blur the button to remove focus after click
    if (event.currentTarget instanceof HTMLElement) {
      event.currentTarget.blur();
    }
  };

  const handleUserFilterClose = (): void => {
    setUserFilterAnchor(null);
  };

  const handleUserToggle = (userId: string): void => {
    // Filter out orphaned user IDs
    if (isOrphanedUserId(userId)) {
      return; // Never toggle orphaned user IDs
    }
    
    // Simple toggle logic - same as account filter
    onUsersChange(
      selectedUsers.includes(userId)
        ? selectedUsers.filter(id => id !== userId)
        : [...selectedUsers, userId]
    );
  };

  const handleSelectAllUsers = (): void => {
    // Filter out orphaned user IDs and fake users (same logic as renderUserFilter)
    const filteredMembers = workspaceMembers.filter(user => 
      !isOrphanedUserId(user.id) && !isFakeUserId(user.id)
    );
    const filteredSelectedUsers = selectedUsers.filter(id => 
      !isOrphanedUserId(id) && !isFakeUserId(id)
    );
    
    // Toggle: if all filtered members are selected, clear to empty (show all)
    // Otherwise, select all filtered members
    // Empty array = show all transactions (no filter) - matches Analytics behavior
    onUsersChange(
      filteredSelectedUsers.length === filteredMembers.length
        ? [] // Empty array = show all (no filter)
        : filteredMembers.map(u => u.id) // Select all filtered members
    );
  };

  const renderAccountFilter = () => {
    if (accounts.length === 0) return null;

    if (isMobile) {
      // Mobile: Select dropdown that opens popover
      return (
        <>
          <FormControl size="small" fullWidth>
            <InputLabel>Account</InputLabel>
            <Select
              value={selectedAccountFilters.length === 0 || selectedAccountFilters.length === accounts.length
                ? "All"
                : selectedAccountFilters.length === 1
                ? selectedAccountFilters[0]
                : "Multiple"}
              label="Account"
              open={false}
              readOnly
              onClick={(e) => {
                handleAccountFilterClick(e);
              }}
              sx={{ cursor: "pointer", pointerEvents: "auto" }}
              renderValue={(value) => {
                if (value === "All") return "All Accounts";
                if (value === "Multiple") return `${selectedAccountFilters.length} Accounts`;
                return getAccountDisplayName(value as string);
              }}
            >
              <MenuItem value="All">All Accounts</MenuItem>
            </Select>
          </FormControl>
          <Popover
            open={Boolean(accountFilterAnchor)}
            anchorEl={accountFilterAnchor}
            onClose={handleAccountFilterClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
          >
              <Box sx={{ p: 1, minWidth: 200, maxWidth: 280, maxHeight: 350, overflowY: "auto", overflowX: "hidden" }}>
              <FormGroup>
                {(() => {
                  // If only one account, disable unchecking
                  const hasOnlyOneAccount = accounts.length === 1;
                  
                  return (
                    <>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={
                              selectedAccountFilters.length === 0 || selectedAccountFilters.length === accounts.length
                            }
                            indeterminate={
                              selectedAccountFilters.length > 0 &&
                              selectedAccountFilters.length < accounts.length
                            }
                            onChange={handleSelectAllAccounts}
                            disabled={hasOnlyOneAccount}
                          />
                        }
                        label={<Typography variant="body2">All Accounts</Typography>}
                        sx={{ mb: 0.5 }}
                      />
                      <Divider sx={{ my: 0.5 }} />
                      {(() => {
                        // Group accounts by their group property
                        const groupedAccounts = accounts.reduce((acc, account) => {
                          const group = account.group || "Other";
                          if (!acc[group]) {
                            acc[group] = [];
                          }
                          acc[group].push(account);
                          return acc;
                        }, {} as Record<string, typeof accounts>);

                        // Sort groups: Cash, Accounts, Cards, Overdrafts, Debts, then Others
                        const groupOrder = ["Cash", "Accounts", "Cards", "Overdrafts", "Debts"];
                        const sortedGroups = Object.keys(groupedAccounts).sort((a, b) => {
                          const indexA = groupOrder.indexOf(a);
                          const indexB = groupOrder.indexOf(b);
                          if (indexA === -1 && indexB === -1) return a.localeCompare(b);
                          if (indexA === -1) return 1;
                          if (indexB === -1) return -1;
                          return indexA - indexB;
                        });

                        return sortedGroups.map((group) => (
                          <Box key={group} sx={{ mb: 0.5 }}>
                            <Typography
                              variant="caption"
                              fontWeight="medium"
                              color="text.secondary"
                              sx={{ px: 1, py: 0.25, display: "block" }}
                            >
                              {group}
                            </Typography>
                            <Box sx={{ display: "flex", flexDirection: "column", width: "100%", minWidth: 0 }}>
                              {groupedAccounts[group].map((account) => (
                                <FormControlLabel
                                  key={account.id}
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={selectedAccountFilters.includes(account.id)}
                                      onChange={() => handleAccountToggle(account.id)}
                                      disabled={hasOnlyOneAccount && selectedAccountFilters.includes(account.id)}
                                    />
                                  }
                            label={
                              <Typography variant="body2" sx={{ fontSize: "0.875rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {account.accountName}
                              </Typography>
                            }
                            sx={{ ml: 1.5, py: 0.25, width: "100%", display: "flex", minWidth: 0 }}
                          />
                              ))}
                            </Box>
                          </Box>
                        ));
                      })()}
                    </>
                  );
                })()}
              </FormGroup>
            </Box>
          </Popover>
        </>
      );
    }

    // Desktop: Use reusable FloatingLabelFilterButton component
    const accountOptions: FilterOption[] = accounts.map(account => ({
      id: account.id,
      label: account.accountName,
      group: account.group,
    }));

    return (
      <FloatingLabelFilterButton
        label="Account"
        options={accountOptions}
        selectedIds={selectedAccountFilters}
        onSelectionChange={onAccountFiltersChange}
        getDisplayName={getAccountDisplayName}
        allSelectedText="All Accounts"
        multipleSelectedText={(count: number) => `${count} Accounts`}
        anchorEl={accountFilterAnchor}
        onOpen={handleAccountFilterClick}
        onClose={handleAccountFilterClose}
        groupOptions={true}
        groupOrder={["Cash", "Accounts", "Cards", "Overdrafts", "Debts"]}
        minWidth={130}
      />
    );
  };

  // Memoize filtered members and selected users to prevent infinite loops
  // Only recalculate when lengths change or when we detect actual content changes
  const { allMembers, filteredSelectedUsers, hasOnlyOneUser } = useMemo(() => {
    if (workspaceMembers.length === 0) {
      return { allMembers: [], filteredSelectedUsers: [], hasOnlyOneUser: true };
    }

    // Filter out orphaned user IDs and fake users (same as account filter logic - simple filtering)
    // Show all workspace members (filter out orphaned, fake users, and users without name/email)
    // Users without name/email are orphaned members and should not appear in the filter
    const filteredMembers = (workspaceMembers || []).filter(user => {
      // Filter out orphaned IDs and fake users
      if (isOrphanedUserId(user.id) || isFakeUserId(user.id)) {
        return false;
      }
      // Filter out users without name or email (orphaned members)
      // Exception: bypass-user is always included (special system user)
      if (user.id === "bypass-user") {
        return true;
      }
      // User must have either name or email to be valid
      return !!(user.name || user.email);
    });
    
    // Disable entire filter only if there are no users
    const hasNoUsers = filteredMembers.length === 0;
    
    const filteredSelected = (selectedUsers || []).filter(id => 
      !isOrphanedUserId(id) && !isFakeUserId(id)
    );

    return {
      allMembers: filteredMembers,
      filteredSelectedUsers: filteredSelected,
      hasOnlyOneUser: hasNoUsers,
    };
  }, [workspaceMembers.length, selectedUsers.length]); // Only depend on lengths to prevent infinite loops

  // Calculate select value - only depend on lengths to prevent infinite loops
  // Extract first user ID separately to avoid array reference dependency
  const firstUserId = filteredSelectedUsers.length === 1 ? filteredSelectedUsers[0] : null;
  
  const selectValue = useMemo(() => {
    if (allMembers.length === 0) return "All";
    if (filteredSelectedUsers.length === 0 || filteredSelectedUsers.length === allMembers.length) return "All";
    if (filteredSelectedUsers.length === 1) return firstUserId || "All";
    return "Multiple";
  }, [allMembers.length, filteredSelectedUsers.length, firstUserId]);

  // Use useCallback to stabilize renderValue function and prevent infinite loops
  const userCount = filteredSelectedUsers.length;
  const renderSelectValue = useCallback((value: string | number) => {
    if (value === "All") return "All Users";
    if (value === "Multiple") return `${userCount} Users`;
    return getUserName(value as string);
  }, [userCount, getUserName]);

  const renderUserFilter = () => {
    if (workspaceMembers.length === 0) return null;

    if (isMobile) {
      // Mobile: Return only FormControl (not wrapped in fragment) so it can expand properly in flex layout
      // Popover will be rendered separately in the return statement
      return (
        <FormControl size="small" fullWidth sx={{ width: "100%" }}>
          <InputLabel>User</InputLabel>
          <Select
            key={`user-select-${allMembers.length}-${filteredSelectedUsers.length}-${firstUserId || 'none'}`}
            value={selectValue}
            label="User"
            open={false}
            readOnly
            onClick={handleUserFilterClick}
            sx={{ 
              width: "100%",
              cursor: hasOnlyOneUser ? "default" : "pointer",
              pointerEvents: hasOnlyOneUser ? "none" : "auto",
              "& .MuiSelect-select": {
                pointerEvents: hasOnlyOneUser ? "none" : "auto",
              },
            }}
            disabled={hasOnlyOneUser} // Disable only if no users
            renderValue={renderSelectValue}
          >
            <MenuItem value="All">All Users</MenuItem>
          </Select>
        </FormControl>
      );
    }

    // Desktop: Use reusable FloatingLabelFilterButton component (same as account filter)
    // Simple - no disabling, just like account filter
    
    const userOptions: FilterOption[] = allMembers.map(user => ({
      id: user.id,
      label: getUserName(user.id),
    }));

    // Handle selection change - same simple logic as account filter
    const handleSelectionChange = (selectedIds: string[]): void => {
      // Filter out orphaned user IDs and fake users from incoming selection
      const cleanSelectedIds = selectedIds.filter(id => 
        !isOrphanedUserId(id) && !isFakeUserId(id)
      );
      
      // If selection matches "all" or "none", use the select all handler
      if (cleanSelectedIds.length === 0 || cleanSelectedIds.length === allMembers.length) {
        handleSelectAllUsers();
        return;
      }
      
      // Otherwise, set the selection directly
      onUsersChange(cleanSelectedIds);
    };

    return (
      <FloatingLabelFilterButton
        label="User"
        options={userOptions}
        selectedIds={filteredSelectedUsers}
        onSelectionChange={handleSelectionChange}
        getDisplayName={getUserName}
        allSelectedText="All Users"
        multipleSelectedText={(count: number) => `${count} Users`}
        anchorEl={userFilterAnchor}
        onOpen={handleUserFilterClick}
        onClose={handleUserFilterClose}
        minWidth={130}
        disabled={hasOnlyOneUser} // Disable only if no users
      />
    );
  };

  // Render user filter Popover for mobile (separate from FormControl for proper flex layout)
  const renderUserFilterPopover = () => {
    if (!isMobile || workspaceMembers.length === 0) return null;
    if (allMembers.length === 0 || hasOnlyOneUser) return null;

    return (
      <Popover
        open={Boolean(userFilterAnchor)}
        anchorEl={userFilterAnchor}
        onClose={handleUserFilterClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Box sx={{ p: 1, minWidth: 200, maxWidth: 280, maxHeight: 350, overflowY: "auto", overflowX: "hidden" }}>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={
                    filteredSelectedUsers.length === 0 || filteredSelectedUsers.length === allMembers.length
                  }
                  indeterminate={
                    filteredSelectedUsers.length > 0 &&
                    filteredSelectedUsers.length < allMembers.length
                  }
                  onChange={handleSelectAllUsers}
                />
              }
              label={<Typography variant="body2">All Users</Typography>}
              sx={{ mb: 0.5 }}
            />
            <Divider sx={{ my: 0.5 }} />
            {allMembers.map((user: { id: string; name?: string; email?: string }) => {
              const displayLabel = getUserName(user.id);
              return (
                <FormControlLabel
                  key={user.id}
                  control={
                    <Checkbox
                      size="small"
                      checked={filteredSelectedUsers.includes(user.id)}
                      onChange={() => handleUserToggle(user.id)}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {displayLabel}
                    </Typography>
                  }
                />
              );
            })}
          </FormGroup>
        </Box>
      </Popover>
    );
  };

  // Return both filters - parent component should handle layout
  return (
    <>
      {showAccountFilter && renderAccountFilter()}
      {showUserFilter && (
        <>
          {showEditedCheckbox ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
              <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
                {renderUserFilter()}
              </Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showOnlyEdited}
                    onChange={(e) => onShowOnlyEditedChange(e.target.checked)}
                  />
                }
                label="Edited"
              />
            </Box>
          ) : (
            renderUserFilter()
          )}
          {isMobile && renderUserFilterPopover()}
        </>
      )}
      {!showUserFilter && showEditedCheckbox && (
        <FormControlLabel
          control={
            <Checkbox
              checked={showOnlyEdited}
              onChange={(e) => onShowOnlyEditedChange(e.target.checked)}
            />
          }
          label="Edited"
        />
      )}
    </>
  );
}
